import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { TOUCH_MODE } from '../game/touch.js';
import { useGameStore, type ChatEntry } from '../game/store.js';

/**
 * The top-left chat bar: recent messages fade away on their own, Enter opens
 * the compose box (a 💬 button on touch, where there is no Enter to press).
 * Messages arrive through the game socket and land in the store; this
 * component only renders and sends — the engine owns the wire.
 *
 * While the compose box has focus the InputManager ignores every keystroke
 * (it checks the event target), so typing "wasd" never strafes the player.
 * The input stays mounted even while closed because iOS only opens the
 * on-screen keyboard for a focus() made synchronously inside the tap gesture.
 */

/** How long a message stays readable while the chat is not being used. */
const FADE_AFTER_MS = 15000;
const IDLE_SHOWN = 5;
const OPEN_SHOWN = 12;

const TEAM_TEXT: Record<number, string> = {
  0: 'text-team-blue',
  1: 'text-team-red',
};

const Line = ({ entry }: { entry: ChatEntry }) => (
  <div className="max-w-[42ch] break-words rounded bg-black/45 px-2 py-0.5 text-[13px] leading-snug backdrop-blur-sm">
    <span
      className={cn(
        'mr-1.5 font-semibold',
        entry.team !== null ? TEAM_TEXT[entry.team] : 'text-ink-muted',
      )}
    >
      {entry.from}
    </span>
    <span className="text-ink/95">{entry.text}</span>
  </div>
);

export const ChatPanel = ({ onSend }: { onSend: (text: string) => void }) => {
  const chat = useGameStore((state) => state.chat);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  // Ticks once a second purely so faded-out messages actually disappear.
  const [now, setNow] = useState(() => performance.now());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(performance.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Enter opens the compose box from anywhere that isn't already a form field.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Enter') return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        return;
      }
      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const close = () => {
    setDraft('');
    setOpen(false);
    inputRef.current?.blur();
  };
  const send = () => {
    const text = draft.trim();
    if (text.length > 0) onSend(text.slice(0, 200));
    close();
  };

  const visible = open
    ? chat.slice(-OPEN_SHOWN)
    : chat.slice(-IDLE_SHOWN).filter((entry) => now - entry.atMs < FADE_AFTER_MS);

  return (
    <div
      className={cn(
        'pointer-events-none fixed left-3 z-20 flex w-72 flex-col gap-1',
        // On touch the corner belongs to the pause/scoreboard buttons.
        TOUCH_MODE ? 'top-16' : 'top-3',
      )}
    >
      {visible.map((entry) => (
        <Line key={entry.id} entry={entry} />
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.code === 'Enter') send();
          if (event.code === 'Escape') close();
          event.stopPropagation();
        }}
        onBlur={close}
        maxLength={200}
        placeholder="Message… (Enter to send)"
        autoComplete="off"
        className={cn(
          'pointer-events-auto mt-1 w-full rounded-md border border-white/20 bg-black/60 px-2.5 py-1.5 text-[13px] text-ink outline-none backdrop-blur focus:border-team-blue',
          // Kept mounted while closed so a touch tap can focus it in-gesture.
          !open && 'pointer-events-none absolute h-0 w-0 border-0 p-0 opacity-0',
        )}
      />
      {!open &&
        (TOUCH_MODE ? (
          <button
            className="pointer-events-auto mt-1 size-9 self-start rounded-full border border-white/20 bg-surface-900/60 text-sm backdrop-blur active:bg-white/25"
            onClick={() => {
              setOpen(true);
              inputRef.current?.focus();
            }}
          >
            💬
          </button>
        ) : (
          chat.length === 0 && (
            <span className="text-[11px] text-ink-muted/70">Enter — chat</span>
          )
        ))}
    </div>
  );
};
